// Modified for ACT+ rDPS integration, 2026.
import type { CombatantData, ExtendData, RaidBuffContribution } from '../api/types';

type BuffKind = 'damage' | 'critical' | 'direct' | 'critical-direct';

interface BuffDefinition {
  name: string;
  group: string;
  kind: BuffKind;
  factor?: number;
  criticalChance?: number;
  directChance?: number;
}

interface ActiveEffect {
  effectId: string;
  sourceId: string;
  targetId: string;
  definition: BuffDefinition;
  providerId?: string;
}

interface Actor {
  id: string;
  name: string;
  level?: number;
  criticalRate?: number;
  directRate?: number;
}

interface DamageEvent {
  actorId: string;
  targetId: string;
  timestamp: number;
  rawDamage: number;
  damage: number;
  critical?: boolean;
  direct?: boolean;
  effects: ActiveEffect[];
}

interface TargetHealthState {
  serverHp: number;
  maxHp: number;
  locked: boolean;
  dead: boolean;
  pendingEvents: number[];
  deathTimestamp?: number;
}

interface DetailAccumulator {
  granted: number;
  received: number;
  rawDamage: number;
  parsedDamage: number;
  grantedByBuff: Map<string, RaidBuffContribution>;
  receivedByBuff: Map<string, RaidBuffContribution>;
}

interface PersistedEstimatorState {
  version: 1;
  actors: Actor[];
  activeEffects: Array<[string, ActiveEffect[]]>;
  petOwners: Array<[string, string]>;
  dotSnapshots: Array<[string, ActiveEffect[]]>;
  damageEvents: DamageEvent[];
  targetHealth: Array<[string, TargetHealthState]>;
  primaryTargetId: string;
  combatStartTimestamp: number;
  lastTimestamp: number;
}

export interface RdpsDetail {
  granted: number;
  received: number;
  rawDamage: number;
  parsedDamage: number;
  net: number;
  grantedByBuff: RaidBuffContribution[];
  receivedByBuff: RaidBuffContribution[];
}

export const BUFFS: Readonly<Record<string, BuffDefinition>> = Object.freeze({
  '4A1': { name: '义结金兰', group: 'brotherhood', kind: 'damage', factor: 1.05 },
  A27: { name: '神秘环', group: 'arcane-circle', kind: 'damage', factor: 1.03 },
  '312': {
    name: '战斗连祷',
    group: 'battle-litany',
    kind: 'critical',
    criticalChance: 0.1,
  },
  '8D': {
    name: '战斗之声',
    group: 'battle-voice',
    kind: 'direct',
    directChance: 0.2,
  },
  B94: {
    name: '光明神的最终乐章',
    group: 'radiant-finale',
    kind: 'damage',
    factor: 1.06,
  },
  '71D': {
    name: '标准舞步结束',
    group: 'standard-finish',
    kind: 'damage',
    factor: 1.05,
  },
  '839': {
    name: '标准舞步结束',
    group: 'standard-finish',
    kind: 'damage',
    factor: 1.05,
  },
  '71E': {
    name: '技巧舞步结束',
    group: 'technical-finish',
    kind: 'damage',
    factor: 1.05,
  },
  '721': {
    name: '进攻之探戈',
    group: 'devilment',
    kind: 'critical-direct',
    criticalChance: 0.2,
    directChance: 0.2,
  },
  '756': { name: '占卜', group: 'divination', kind: 'damage', factor: 1.06 },
  F2F: { name: '均衡', group: 'the-balance', kind: 'damage', factor: 1.06 },
  F31: { name: '长矛', group: 'the-spear', kind: 'damage', factor: 1.06 },
  '4C5': {
    name: '连环计',
    group: 'chain-stratagem',
    kind: 'critical',
    criticalChance: 0.1,
  },
  A8F: { name: '灼热之光', group: 'searing-light', kind: 'damage', factor: 1.05 },
  '511': { name: '鼓励', group: 'embolden', kind: 'damage', factor: 1.05 },
  F09: { name: '毒盛之术', group: 'dokumori', kind: 'damage', factor: 1.05 },
  E65: { name: '星空构想', group: 'starry-muse', kind: 'damage', factor: 1.05 },
});

const ATTACK_FLAGS = new Set(['01', '03', '05', '06']);

const normalizeId = (value: unknown) => String(value ?? '').toUpperCase();
export const normalizeName = (value: unknown) =>
  String(value ?? '')
    .trim()
    .toLocaleLowerCase();
const isZeroId = (value: unknown) => /^0+$/.test(normalizeId(value));
const isPlayerId = (id: unknown) => normalizeId(id).startsWith('1');

export const unscrambleDamage = (field: unknown) => {
  if (field === undefined || field === null) {
    return 0;
  }
  const value = String(field);
  const len = value.length;
  if (len <= 4) {
    return 0;
  }
  let damage = Number.parseInt(value.slice(0, len - 4), 16);
  if (!Number.isFinite(damage)) {
    return 0;
  }
  if (value[len - 4] === '4') {
    const rightDamage = Number.parseInt(value.slice(len - 2), 16);
    damage = damage - rightDamage + (rightDamage << 16);
  }
  return Math.max(0, damage);
};

export const abilityDamage = (line: unknown[]) => {
  let total = 0;
  for (let i = 8; i <= 22; i += 2) {
    const flags = String(line[i] ?? '')
      .slice(-2)
      .padStart(2, '0')
      .toUpperCase();
    if (ATTACK_FLAGS.has(flags)) {
      total += unscrambleDamage(line[i + 1]);
    }
  }
  return total;
};

interface DamageResult {
  damage: number;
  critical: boolean;
  direct: boolean;
}

function abilityDamageResults(line: unknown[]): DamageResult[] {
  const results: DamageResult[] = [];
  for (let i = 8; i <= 22; i += 2) {
    const rawFlags = String(line[i] ?? '');
    const effectType = rawFlags.slice(-2).padStart(2, '0').toUpperCase();
    if (!ATTACK_FLAGS.has(effectType)) continue;
    const damage = unscrambleDamage(line[i + 1]);
    if (damage <= 0) continue;
    const flags = Number.parseInt(rawFlags, 16);
    results.push({
      damage,
      critical: Number.isFinite(flags) && (flags & 0x2000) !== 0,
      direct: Number.isFinite(flags) && (flags & 0x4000) !== 0,
    });
  }
  return results;
}

const intField = (value: unknown) => {
  const parsed = Number.parseInt(String(value ?? ''), 10);
  return Number.isFinite(parsed) ? parsed : 0;
};

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

export class RdpsEstimator {
  private readonly buffDefinitions: Readonly<Record<string, BuffDefinition>>;
  private actors = new Map<string, Actor>();
  private activeEffects = new Map<string, Map<string, ActiveEffect>>();
  private petOwners = new Map<string, string>();
  private dotSnapshots = new Map<string, ActiveEffect[]>();
  private damageEvents: DamageEvent[] = [];
  private targetHealth = new Map<string, TargetHealthState>();
  private primaryTargetId = '';
  private combatStartTimestamp = 0;
  private lastTimestamp = 0;
  private detailsCache: Map<string, DetailAccumulator> | null = null;

  constructor(buffDefinitions: Readonly<Record<string, BuffDefinition>> = BUFFS) {
    this.buffDefinitions = buffDefinitions;
  }

  reset() {
    this.actors.clear();
    this.activeEffects.clear();
    this.petOwners.clear();
    this.dotSnapshots.clear();
    this.damageEvents = [];
    this.targetHealth.clear();
    this.primaryTargetId = '';
    this.combatStartTimestamp = 0;
    this.lastTimestamp = 0;
    this.detailsCache = null;
  }

  beginCombat(timestamp = Date.now()) {
    this.reset();
    if (Number.isFinite(timestamp) && timestamp > 0) {
      this.combatStartTimestamp = timestamp;
    }
  }

  exportState(): PersistedEstimatorState {
    return {
      version: 1,
      actors: [...this.actors.values()].map((actor) => ({ ...actor })),
      activeEffects: [...this.activeEffects].map(([targetId, effects]) => [
        targetId,
        [...effects.values()].map((effect) => ({
          ...effect,
          definition: { ...effect.definition },
        })),
      ]),
      petOwners: [...this.petOwners],
      dotSnapshots: [...this.dotSnapshots].map(([key, effects]) => [
        key,
        effects.map((effect) => ({ ...effect, definition: { ...effect.definition } })),
      ]),
      damageEvents: this.damageEvents.map((event) => ({
        ...event,
        effects: event.effects.map((effect) => ({
          ...effect,
          definition: { ...effect.definition },
        })),
      })),
      targetHealth: [...this.targetHealth].map(([targetId, state]) => [
        targetId,
        { ...state, pendingEvents: [...state.pendingEvents] },
      ]),
      primaryTargetId: this.primaryTargetId,
      combatStartTimestamp: this.combatStartTimestamp,
      lastTimestamp: this.lastTimestamp,
    };
  }

  restoreState(value: unknown) {
    if (!value || typeof value !== 'object') return false;
    const state = value as Partial<PersistedEstimatorState>;
    if (
      state.version !== 1 ||
      !Array.isArray(state.actors) ||
      !Array.isArray(state.activeEffects) ||
      !Array.isArray(state.petOwners) ||
      !Array.isArray(state.dotSnapshots) ||
      !Array.isArray(state.damageEvents) ||
      !Array.isArray(state.targetHealth)
    ) {
      return false;
    }
    try {
      this.reset();
      this.actors = new Map(state.actors.map((actor) => [actor.id, { ...actor }]));
      this.activeEffects = new Map(
        state.activeEffects.map(([targetId, effects]) => [
          targetId,
          new Map(
            effects.map((effect) => [
              this.effectKey(effect.effectId, effect.sourceId),
              { ...effect, definition: { ...effect.definition } },
            ])
          ),
        ])
      );
      this.petOwners = new Map(state.petOwners);
      this.dotSnapshots = new Map(
        state.dotSnapshots.map(([key, effects]) => [
          key,
          effects.map((effect) => ({ ...effect, definition: { ...effect.definition } })),
        ])
      );
      this.damageEvents = state.damageEvents.map((event) => ({
        ...event,
        effects: event.effects.map((effect) => ({
          ...effect,
          definition: { ...effect.definition },
        })),
      }));
      this.targetHealth = new Map(
        state.targetHealth.map(([targetId, target]) => [
          targetId,
          { ...target, pendingEvents: [...target.pendingEvents] },
        ])
      );
      this.primaryTargetId = String(state.primaryTargetId ?? '');
      this.combatStartTimestamp = Number(state.combatStartTimestamp) || 0;
      this.lastTimestamp = Number(state.lastTimestamp) || 0;
      this.detailsCache = null;
      return true;
    } catch {
      this.reset();
      return false;
    }
  }

  processRawLine(rawLine: unknown) {
    if (rawLine) {
      this.processLine(
        String(rawLine)
          .replace(/[\r\n]+$/, '')
          .split('|')
      );
    }
  }

  processLine(line: unknown[]) {
    if (!Array.isArray(line) || line.length < 2) {
      return;
    }
    const timestamp = Date.parse(String(line[1] ?? ''));
    if (Number.isFinite(timestamp)) {
      this.lastTimestamp = timestamp;
    }

    switch (line[0]) {
      case '02':
        this.rememberActor(line[2], String(line[3] ?? ''));
        break;
      case '03':
        this.onAddedCombatant(line);
        break;
      case '21':
      case '22':
        this.onAbility(line);
        break;
      case '24':
        this.onDot(line);
        break;
      case '26':
        this.onGainEffect(line);
        break;
      case '30':
        this.onLoseEffect(line);
        break;
      case '37':
        this.onHealthUpdate(line, 5, 6);
        break;
      case '38':
        this.onHealthUpdate(line, 5, 6);
        break;
      case '39':
        this.onHealthUpdate(line, 4, 5);
        break;
      case '25':
        this.onDeath(line);
        break;
    }
  }

  private newActor(id: string, name = ''): Actor {
    return {
      id,
      name,
    };
  }

  private rememberActor(idValue: unknown, name = '') {
    const id = normalizeId(idValue);
    if (!id) {
      return undefined;
    }
    let actor = this.actors.get(id);
    if (!actor) {
      actor = this.newActor(id, name);
      this.actors.set(id, actor);
    } else if (name && (!actor.name || actor.name.startsWith('0x'))) {
      actor.name = name;
      this.detailsCache = null;
    }
    return actor;
  }

  private onAddedCombatant(line: unknown[]) {
    const id = normalizeId(line[2]);
    const ownerId = normalizeId(line[6]);
    if (ownerId && !isZeroId(ownerId)) {
      this.petOwners.set(id, ownerId);
    }
    if (isPlayerId(id)) {
      const actor = this.rememberActor(id, String(line[3] ?? ''));
      const level = Number.parseInt(String(line[5] ?? ''), 16);
      if (actor && Number.isFinite(level) && level > 0) {
        actor.level = level;
      }
    }
  }

  private resolveDamageActor(
    sourceIdValue: unknown,
    sourceNameValue: unknown,
    ownerIdValue?: unknown,
    ownerNameValue?: unknown
  ) {
    const sourceId = normalizeId(sourceIdValue);
    const explicitOwner = normalizeId(ownerIdValue);
    if (explicitOwner && !isZeroId(explicitOwner)) {
      this.petOwners.set(sourceId, explicitOwner);
      this.rememberActor(explicitOwner, String(ownerNameValue ?? ''));
    }
    const actorId = this.petOwners.get(sourceId) ?? sourceId;
    if (!isPlayerId(actorId)) {
      return undefined;
    }
    return this.rememberActor(
      actorId,
      String(actorId === sourceId ? sourceNameValue : (ownerNameValue ?? ''))
    );
  }

  private effectMap(targetIdValue: unknown, create = false) {
    const targetId = normalizeId(targetIdValue);
    let effects = this.activeEffects.get(targetId);
    if (!effects && create) {
      effects = new Map();
      this.activeEffects.set(targetId, effects);
    }
    return effects;
  }

  private effectKey(effectId: unknown, sourceId: unknown) {
    return `${normalizeId(effectId)}:${normalizeId(sourceId)}`;
  }

  private dotKey(targetId: unknown, sourceId: unknown, effectId: unknown) {
    return `${normalizeId(targetId)}:${normalizeId(sourceId)}:${normalizeId(effectId)}`;
  }

  private onGainEffect(line: unknown[]) {
    const effectId = normalizeId(line[2]);
    const sourceId = normalizeId(line[5]);
    const targetId = normalizeId(line[7]);
    if (isPlayerId(sourceId)) {
      this.rememberActor(sourceId, String(line[6] ?? ''));
    }
    if (isPlayerId(targetId)) {
      this.rememberActor(targetId, String(line[8] ?? ''));
    }

    const definition = this.buffDefinitions[effectId];
    if (definition) {
      this.effectMap(targetId, true)?.set(this.effectKey(effectId, sourceId), {
        effectId,
        sourceId,
        targetId,
        definition,
      });
    }

    if (isPlayerId(sourceId) && targetId.startsWith('4') && !definition) {
      const snapshot = this.collectExternalEffects(sourceId, targetId, sourceId);
      this.dotSnapshots.set(this.dotKey(targetId, sourceId, effectId), snapshot);
    }
  }

  private onLoseEffect(line: unknown[]) {
    const effectId = normalizeId(line[2]);
    const sourceId = normalizeId(line[5]);
    const targetId = normalizeId(line[7]);
    const effects = this.effectMap(targetId);
    effects?.delete(this.effectKey(effectId, sourceId));
    if (effects?.size === 0) {
      this.activeEffects.delete(targetId);
    }
    this.dotSnapshots.delete(this.dotKey(targetId, sourceId, effectId));
  }

  private onAbility(line: unknown[]) {
    const actor = this.resolveDamageActor(line[2], line[3], line[47], line[48]);
    const targetId = normalizeId(line[6]);
    if (!actor || !targetId.startsWith('4')) return;

    this.syncTargetHealth(targetId, intField(line[24]), intField(line[25]));
    const results = abilityDamageResults(line);
    if (!results.length) return;

    const effects = this.collectExternalEffects(line[2], line[6], actor.id);
    for (const result of results) {
      this.recordDamage(actor, targetId, result.damage, effects, result.critical, result.direct);
    }
  }

  private onDot(line: unknown[]) {
    if (line[4] !== 'DoT') {
      return;
    }
    const damage = Number.parseInt(String(line[6] ?? '0'), 16);
    if (!Number.isFinite(damage) || damage <= 0) {
      return;
    }
    const sourceId = normalizeId(line[17]);
    const actor = this.resolveDamageActor(sourceId, line[18]);
    if (!actor) {
      return;
    }
    const targetId = normalizeId(line[2]);
    if (!targetId.startsWith('4')) return;
    this.syncTargetHealth(targetId, intField(line[7]), intField(line[8]));
    const effectId = normalizeId(line[5]);
    const snapshot = this.dotSnapshots.get(this.dotKey(targetId, sourceId, effectId));
    const effects = snapshot ?? this.collectExternalEffects(sourceId, targetId, actor.id);
    this.recordDamage(actor, targetId, damage, effects);
  }

  private onHealthUpdate(line: unknown[], hpIndex: number, maxHpIndex: number) {
    const targetId = normalizeId(line[2]);
    if (!targetId.startsWith('4')) return;
    this.syncTargetHealth(targetId, intField(line[hpIndex]), intField(line[maxHpIndex]));
  }

  private onDeath(line: unknown[]) {
    const targetId = normalizeId(line[2]);
    if (!targetId.startsWith('4')) return;
    const state = this.targetHealth.get(targetId);
    if (state) {
      if (state.locked) {
        this.reconcilePending(state, 0);
      } else {
        this.reconcilePending(state, state.serverHp);
      }
      state.serverHp = 0;
      state.dead = true;
      state.deathTimestamp = this.lastTimestamp;
    }
  }

  private syncTargetHealth(targetId: string, hp: number, maxHp: number) {
    if (maxHp <= 0 || hp < 0) return;
    let state = this.targetHealth.get(targetId);
    if (!state) {
      state = {
        serverHp: hp,
        maxHp,
        locked: false,
        dead: hp === 0,
        pendingEvents: [],
      };
      this.targetHealth.set(targetId, state);
    } else {
      state.maxHp = Math.max(state.maxHp, maxHp);
      if (hp < state.serverHp) {
        // Ability packets carry delayed, pre-hit HP snapshots. A smaller
        // snapshot confirms the pending damage, but its delta cannot safely be
        // used to scale individual hits because several packets may share it.
        if (hp === 0) {
          this.reconcilePending(state, state.serverHp);
        } else {
          state.pendingEvents = [];
        }
        state.serverHp = hp;
      } else if (hp > state.serverHp) {
        // Hostile HP snapshots can arrive out of order. Only accept an increase
        // after a dead/locked phase or when no damage is waiting for confirmation.
        if ((state.dead || state.locked || state.pendingEvents.length === 0) && hp > 0) {
          state.serverHp = hp;
          state.dead = false;
          state.locked = false;
          state.pendingEvents = [];
        }
      } else if (state.pendingEvents.length > 0 && hp > 0 && hp / state.maxHp <= 0.0011) {
        this.reconcilePending(state, 0);
        state.locked = true;
      }
      if (hp === 0) {
        this.reconcilePending(state, 0);
        state.dead = true;
      }
    }

    if (!this.primaryTargetId) {
      this.primaryTargetId = targetId;
    } else {
      const primaryMaxHp = this.targetHealth.get(this.primaryTargetId)?.maxHp ?? 0;
      if (state.maxHp > primaryMaxHp) this.primaryTargetId = targetId;
    }
  }

  private reconcilePending(state: TargetHealthState, confirmedDamage: number) {
    if (!state.pendingEvents.length) return;
    const pending = state.pendingEvents
      .map((index) => this.damageEvents[index])
      .filter((event): event is DamageEvent => Boolean(event));
    const predictedDamage = pending.reduce((sum, event) => sum + event.damage, 0);
    if (predictedDamage > 0 && confirmedDamage < predictedDamage) {
      const scale = clamp(confirmedDamage / predictedDamage, 0, 1);
      for (const event of pending) event.damage *= scale;
      this.detailsCache = null;
    }
    state.pendingEvents = [];
  }

  private recordDamage(
    actor: Actor,
    targetId: string,
    rawDamage: number,
    effects: ActiveEffect[],
    critical?: boolean,
    direct?: boolean
  ) {
    const state = this.targetHealth.get(targetId);
    let damage = rawDamage;
    if (state) {
      if (state.dead || state.locked || state.serverHp <= 0) {
        damage = 0;
      }
    }
    const event: DamageEvent = {
      actorId: actor.id,
      targetId,
      timestamp: this.lastTimestamp,
      rawDamage,
      damage,
      critical,
      direct,
      effects: effects.map((effect) => ({ ...effect })),
    };
    const eventIndex = this.damageEvents.push(event) - 1;
    if (state) state.pendingEvents.push(eventIndex);
    this.detailsCache = null;
  }

  private collectExternalEffects(
    sourceIdValue: unknown,
    targetIdValue: unknown,
    damageActorIdValue: unknown
  ) {
    const sourceId = normalizeId(sourceIdValue);
    const targetId = normalizeId(targetIdValue);
    const damageActorId = normalizeId(damageActorIdValue);
    const ownerId = this.petOwners.get(sourceId);
    const candidates: ActiveEffect[] = [];
    const append = (id: string) => {
      const effects = this.effectMap(id);
      if (effects) {
        candidates.push(...effects.values());
      }
    };
    append(sourceId);
    if (ownerId && ownerId !== sourceId) {
      append(ownerId);
    }
    append(targetId);

    const deduped = new Map<string, ActiveEffect>();
    for (const effect of candidates) {
      const providerId = this.petOwners.get(effect.sourceId) ?? effect.sourceId;
      if (!isPlayerId(providerId) || providerId === damageActorId) {
        continue;
      }
      const key = `${effect.definition.group}:${providerId}`;
      const previous = deduped.get(key);
      const strength = (definition: BuffDefinition) =>
        (definition.factor ?? 1) -
        1 +
        (definition.criticalChance ?? 0) +
        (definition.directChance ?? 0);
      if (!previous || strength(previous.definition) < strength(effect.definition)) {
        deduped.set(key, { ...effect, providerId });
      }
    }
    return [...deduped.values()];
  }

  setObservedRates(combatants: CombatantData[], primaryPlayerName = '', configuredPlayerName = '') {
    let changed = false;
    for (const player of combatants) {
      const lookupName =
        player.name === 'YOU'
          ? primaryPlayerName || configuredPlayerName || player.name
          : player.name;
      if (player.hits < 10) continue;
      const criticalRate = Number.parseFloat(player.critHitPct) / 100;
      const directRate = Number.parseFloat(player.directHitPct) / 100;
      for (const actor of this.actors.values()) {
        if (normalizeName(actor.name) !== normalizeName(lookupName)) continue;
        if (Number.isFinite(criticalRate)) {
          const next = clamp(criticalRate, 0, 0.95);
          changed ||= actor.criticalRate !== next;
          actor.criticalRate = next;
        }
        if (Number.isFinite(directRate)) {
          const next = clamp(directRate, 0, 0.95);
          changed ||= actor.directRate !== next;
          actor.directRate = next;
        }
      }
    }
    if (changed) this.detailsCache = null;
  }

  private actorRates(actor: Actor) {
    const level = actor.level ?? 100;
    const criticalMultiplier = level <= 70 ? 1.55 : level <= 90 ? 1.58 : 1.6;
    return {
      criticalRate: actor.criticalRate ?? (level <= 70 ? 0.2 : 0.25),
      directRate: actor.directRate ?? (level <= 70 ? 0.2 : 0.25),
      criticalMultiplier,
      directMultiplier: 1.25,
    };
  }

  private eventContributions(event: DamageEvent, actor: Actor) {
    const contributions: Array<{ effect: ActiveEffect; amount: number }> = [];
    if (event.damage <= 0 || !event.effects.length) return contributions;

    const fixedEffects = event.effects.filter(
      (effect) => effect.definition.kind === 'damage' && (effect.definition.factor ?? 1) > 1
    );
    let totalFactor = 1;
    let totalWeight = 0;
    const weighted: Array<{ effect: ActiveEffect; weight: number }> = [];
    for (const effect of fixedEffects) {
      const factor = Number(effect.definition.factor);
      if (!Number.isFinite(factor) || factor <= 1) continue;
      const weight = Math.log(factor);
      totalFactor *= factor;
      totalWeight += weight;
      weighted.push({ effect, weight });
    }
    const damageWithoutFixedBuffs = event.damage / totalFactor;
    if (totalFactor > 1 && totalWeight > 0) {
      const extraDamage = event.damage - damageWithoutFixedBuffs;
      for (const { effect, weight } of weighted) {
        contributions.push({ effect, amount: (extraDamage * weight) / totalWeight });
      }
    }

    const criticalEffects = event.effects.filter(
      (effect) => (effect.definition.criticalChance ?? 0) > 0
    );
    const directEffects = event.effects.filter(
      (effect) => (effect.definition.directChance ?? 0) > 0
    );
    if (!criticalEffects.length && !directEffects.length) return contributions;

    const rates = this.actorRates(actor);
    const externalCritical = criticalEffects.reduce(
      (sum, effect) => sum + (effect.definition.criticalChance ?? 0),
      0
    );
    const externalDirect = directEffects.reduce(
      (sum, effect) => sum + (effect.definition.directChance ?? 0),
      0
    );

    let criticalRemoval = 0;
    let directRemoval = 0;
    if (event.critical === undefined) {
      const criticalBonus = rates.criticalMultiplier - 1;
      const directBonus = rates.directMultiplier - 1;
      const criticalExpected =
        (1 + (rates.criticalRate + externalCritical) * criticalBonus) /
        (1 + rates.criticalRate * criticalBonus);
      const directExpected =
        (1 + (rates.directRate + externalDirect) * directBonus) /
        (1 + rates.directRate * directBonus);
      criticalRemoval = externalCritical > 0 ? 1 - 1 / criticalExpected : 0;
      directRemoval = externalDirect > 0 ? 1 - 1 / directExpected : 0;
    } else {
      if (event.critical && externalCritical > 0) {
        const totalCriticalRate = clamp(rates.criticalRate + externalCritical, 0.001, 1);
        criticalRemoval =
          clamp(externalCritical / totalCriticalRate, 0, 1) * (1 - 1 / rates.criticalMultiplier);
      }
      if (event.direct && externalDirect > 0) {
        const totalDirectRate = clamp(rates.directRate + externalDirect, 0.001, 1);
        directRemoval =
          clamp(externalDirect / totalDirectRate, 0, 1) * (1 - 1 / rates.directMultiplier);
      }
    }

    const criticalWeight = criticalRemoval > 0 ? -Math.log(1 - criticalRemoval) : 0;
    const directWeight = directRemoval > 0 ? -Math.log(1 - directRemoval) : 0;
    const chanceWeight = criticalWeight + directWeight;
    if (chanceWeight <= 0) return contributions;

    const chanceExtra = damageWithoutFixedBuffs * (1 - (1 - criticalRemoval) * (1 - directRemoval));
    if (criticalWeight > 0 && externalCritical > 0) {
      const categoryAmount = (chanceExtra * criticalWeight) / chanceWeight;
      for (const effect of criticalEffects) {
        contributions.push({
          effect,
          amount: categoryAmount * ((effect.definition.criticalChance ?? 0) / externalCritical),
        });
      }
    }
    if (directWeight > 0 && externalDirect > 0) {
      const categoryAmount = (chanceExtra * directWeight) / chanceWeight;
      for (const effect of directEffects) {
        contributions.push({
          effect,
          amount: categoryAmount * ((effect.definition.directChance ?? 0) / externalDirect),
        });
      }
    }
    return contributions;
  }

  private addBreakdown(
    map: Map<string, RaidBuffContribution>,
    key: string,
    metadata: RaidBuffContribution,
    amount: number
  ) {
    const entry = map.get(key) ?? { ...metadata, amount: 0, dps: 0 };
    entry.amount += amount;
    if (!entry.providerName && metadata.providerName) {
      entry.providerName = metadata.providerName;
    }
    map.set(key, entry);
  }

  private mergeBreakdown(
    target: Map<string, RaidBuffContribution>,
    source: Map<string, RaidBuffContribution>
  ) {
    for (const [key, entry] of source) {
      this.addBreakdown(target, key, entry, entry.amount);
    }
  }

  private newDetailAccumulator(): DetailAccumulator {
    return {
      granted: 0,
      received: 0,
      rawDamage: 0,
      parsedDamage: 0,
      grantedByBuff: new Map(),
      receivedByBuff: new Map(),
    };
  }

  private buildDetails() {
    if (this.detailsCache) return this.detailsCache;
    const details = new Map<string, DetailAccumulator>();
    const getDetail = (actorId: string) => {
      let detail = details.get(actorId);
      if (!detail) {
        detail = this.newDetailAccumulator();
        details.set(actorId, detail);
      }
      return detail;
    };

    for (const event of this.damageEvents) {
      const actor = this.actors.get(event.actorId);
      if (!actor) continue;
      const actorDetail = getDetail(actor.id);
      actorDetail.rawDamage += event.rawDamage;
      actorDetail.parsedDamage += event.damage;
      for (const { effect, amount } of this.eventContributions(event, actor)) {
        if (amount <= 0) continue;
        const provider = this.rememberActor(effect.providerId);
        if (!provider) continue;
        const providerDetail = getDetail(provider.id);
        const metadata: RaidBuffContribution = {
          group: effect.definition.group,
          buffName: effect.definition.name,
          providerId: provider.id,
          providerName: provider.name,
          amount: 0,
          dps: 0,
        };
        actorDetail.received += amount;
        providerDetail.granted += amount;
        this.addBreakdown(
          actorDetail.receivedByBuff,
          `${effect.definition.group}:${provider.id}`,
          metadata,
          amount
        );
        this.addBreakdown(providerDetail.grantedByBuff, effect.definition.group, metadata, amount);
      }
    }
    this.detailsCache = details;
    return details;
  }

  durationSeconds(fallbackDuration: number) {
    const firstDamage = this.damageEvents.find((event) => event.damage > 0)?.timestamp ?? 0;
    const start =
      this.combatStartTimestamp > 0 && firstDamage > 0
        ? Math.min(this.combatStartTimestamp, firstDamage)
        : this.combatStartTimestamp || firstDamage;
    const end = this.targetHealth.get(this.primaryTargetId)?.deathTimestamp ?? 0;
    if (start > 0 && end > start) return Math.max(1, (end - start) / 1000);
    return Math.max(1, fallbackDuration || 0);
  }

  detailForName(name: unknown): RdpsDetail {
    const normalized = normalizeName(name);
    let granted = 0;
    let received = 0;
    let rawDamage = 0;
    let parsedDamage = 0;
    const grantedByBuff = new Map<string, RaidBuffContribution>();
    const receivedByBuff = new Map<string, RaidBuffContribution>();
    const details = this.buildDetails();
    for (const actor of this.actors.values()) {
      if (normalizeName(actor.name) !== normalized) {
        continue;
      }
      const detail = details.get(actor.id);
      if (!detail) continue;
      granted += detail.granted;
      received += detail.received;
      rawDamage += detail.rawDamage;
      parsedDamage += detail.parsedDamage;
      this.mergeBreakdown(grantedByBuff, detail.grantedByBuff);
      this.mergeBreakdown(receivedByBuff, detail.receivedByBuff);
    }
    const rows = (map: Map<string, RaidBuffContribution>) =>
      [...map.values()].map((entry) => ({ ...entry })).sort((a, b) => b.amount - a.amount);
    return {
      granted,
      received,
      rawDamage,
      parsedDamage,
      net: granted - received,
      grantedByBuff: rows(grantedByBuff),
      receivedByBuff: rows(receivedByBuff),
    };
  }
}

function withDps(rows: RaidBuffContribution[], duration: number) {
  return rows.map((row) => ({ ...row, dps: Math.round(row.amount / duration) }));
}

export function applyRdpsToCombatData(
  data: ExtendData,
  estimator: RdpsEstimator,
  primaryPlayerName = '',
  configuredPlayerName = ''
): ExtendData {
  estimator.setObservedRates(data.combatant, primaryPlayerName, configuredPlayerName);
  const duration = estimator.durationSeconds(data.encounter.durationSeconds);
  const combatant = data.combatant.map((player): CombatantData => {
    const lookupName =
      player.name === 'YOU'
        ? primaryPlayerName || configuredPlayerName || player.name
        : player.name;
    const detail = estimator.detailForName(lookupName);
    const effectiveDamageRatio =
      detail.rawDamage > 0 ? clamp(detail.parsedDamage / detail.rawDamage, 0, 1) : 1;
    const baseDps =
      player.damage > 0 ? (player.damage * effectiveDamageRatio) / duration : player.dps;
    const rdpsGiven = Math.round(detail.granted / duration);
    const rdpsReceived = Math.round(detail.received / duration);
    return {
      ...player,
      rdps: Math.round(baseDps + (detail.net / duration || 0)),
      rdpsGiven,
      rdpsReceived,
      rdpsGivenByBuff: withDps(detail.grantedByBuff, duration),
      rdpsReceivedByBuff: withDps(detail.receivedByBuff, duration),
    };
  });
  return {
    ...data,
    encounter: {
      ...data.encounter,
      rdps: combatant.reduce((sum, player) => sum + player.rdps, 0),
    },
    combatant,
    limitBreak: data.limitBreak ? { ...data.limitBreak, rdps: data.limitBreak.dps } : undefined,
  };
}
